import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Button,
  Card,
  CardBody,
  useToast,
  HStack,
  VStack,
  Text,
  Grid,
  GridItem,
  Badge,
  Input,
  Textarea,
  InputGroup,
  InputLeftElement,
  Select,
  Icon,
  useBreakpointValue,
  SimpleGrid,
  Flex,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import { FiRefreshCw, FiSearch, FiAirplay, FiNavigation, FiCoffee, FiMap, FiShoppingBag, FiGlobe, FiShoppingCart, FiPackage, FiPlus, FiSend } from 'react-icons/fi';
import { bpProductNewApi, bpProductRetailApi, packageApi } from '../config/api';
import SkeletonBlock from '../components/bundling/SkeletonBlock';

const MEMBER_CODE_MAPPING = {
  'api': 'Injourney Airports',
  'ias': 'Injourney Aviation Services',
  'twc': 'Injourney Destination Management',
  'hin': 'Injourney Hospitality',
  'itdc': 'Injourney Tourism Development',
};

const inventoryCategories = [
  { id: 'all', label: 'Semua Kategori', icon: null },
  { id: 'airports', label: 'Injourney Airports', icon: FiAirplay },
  { id: 'aviation-services', label: 'Injourney Aviation Services', icon: FiNavigation },
  { id: 'hospitality', label: 'Injourney Hospitality', icon: FiCoffee },
  { id: 'destination-management', label: 'Injourney Destination Management', icon: FiMap },
  { id: 'retail', label: 'Injourney Retail', icon: FiShoppingBag },
  { id: 'tourism-development', label: 'Injourney Tourism Development', icon: FiGlobe },
];

const BrowseInventory = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [apiStatusFilter, setApiStatusFilter] = useState('all'); // all, published, draft
  const [publishTarget, setPublishTarget] = useState(null);
  const [publishPrice, setPublishPrice] = useState(0);
  const [publishPriceType, setPublishPriceType] = useState('publish'); // publish, corporate, agent
  const [publishQuota, setPublishQuota] = useState(10);
  const [publishValidFrom, setPublishValidFrom] = useState('');
  const [publishValidTo, setPublishValidTo] = useState('');
  const [publishLocation, setPublishLocation] = useState('');
  const [publishPriceIncludes, setPublishPriceIncludes] = useState('');
  const [publishPriceExcludes, setPublishPriceExcludes] = useState('');
  const { isOpen: isPublishOpen, onOpen: onPublishOpen, onClose: onPublishClose } = useDisclosure();
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [catalogPage, setCatalogPage] = useState(1);
  const [packages, setPackages] = useState([]);
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { isOpen: isAddToPackageOpen, onOpen: onAddToPackageOpen, onClose: onAddToPackageClose } = useDisclosure();
  const { isOpen: isNewPackageOpen, onOpen: onNewPackageOpen, onClose: onNewPackageClose } = useDisclosure();
  const { isOpen: isBulkAddOpen, onOpen: onBulkAddOpen, onClose: onBulkAddClose } = useDisclosure();
  
  const [packageFormData, setPackageFormData] = useState({
    package_name: '',
    description: '',
    valid_date: '',
    valid_end_date: '',
    quota: '',
    pic_member: '',
  });
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [bulkPackageId, setBulkPackageId] = useState('');
  const [bulkQuantity, setBulkQuantity] = useState(1);
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemPrice, setItemPrice] = useState(0);
  const [priceType, setPriceType] = useState('bundle'); // 'bundle' or 'discount'
  const [discountPercent, setDiscountPercent] = useState(0);
  const [selectedPriceType, setSelectedPriceType] = useState('base'); // 'base', 'corporate', 'agent'

  // Helper function to map member code to category
  const getCategoryFromMemberCode = (memberCode) => {
    if (!memberCode) return 'retail';
    const memberLower = memberCode.toLowerCase();
    if (memberLower === 'api') return 'airports';
    if (memberLower === 'ias') return 'aviation-services';
    if (memberLower === 'twc') return 'destination-management';
    if (memberLower === 'hin') return 'hospitality';
    if (memberLower === 'itdc') return 'tourism-development';
    return 'retail';
  };

  // Helper function to get product status based on dates
  const getProductStatus = (product) => {
    if (product.product_type === 'retail') {
      return 'active'; // Retail products use BPPRODUCT_STATUS which is already filtered
    }
    
    if (!product.BPPRODUCT_STARTDATE || !product.BPPRODUCT_ENDDATE) {
      return 'inactive';
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(product.BPPRODUCT_STARTDATE);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(product.BPPRODUCT_ENDDATE);
    endDate.setHours(0, 0, 0, 0);
    
    if (today >= startDate && today <= endDate) {
      return 'active';
    } else if (today < startDate) {
      return 'pending';
    } else {
      return 'inactive';
    }
  };

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const response = await bpProductNewApi.get('/browse', { params });
      
      if (response.data.status === 200) {
        let products = response.data.data || [];
        
        // Map products to inventory format
        const mappedProducts = products.map(product => {
          const category = getCategoryFromMemberCode(product.MEMBER_CODE) || (product.product_type === 'retail' ? 'retail' : 'all');
          const status = getProductStatus(product);
          
          // For retail products, use different location logic
          const location = product.product_type === 'retail' 
            ? 'Injourney Retail'
            : (product.COMPANY_NAME || MEMBER_CODE_MAPPING[product.MEMBER_CODE?.toLowerCase()] || product.MEMBER_CODE || '-');
          
          return {
            id: product.BPPRODUCT_ID,
            name: product.BPPRODUCT_NAME || '-',
            category: category,
            description: product.BPPRODUCT_DESC || '-',
            status: status,
            quantity: product.BPPRODUCT_QUANTITY || 0,
            location: location,
            price: parseFloat(product.BPPRODUCT_PUBLISHPRICE) || 0,
            originalPrice: parseFloat(product.BPPRODUCT_PUBLISHPRICE) || 0,
            productCode: product.BPPRODUCT_CODE,
            productType: product.product_type,
            startDate: product.BPPRODUCT_STARTDATE,
            endDate: product.BPPRODUCT_ENDDATE,
            memberCode: product.MEMBER_CODE,
            categoryName: product.BPPRODUCT_CATEGORY,
            corporatePrice: parseFloat(product.BPPRODUCT_CORPPRICE) || null,
            agentPrice: parseFloat(product.BPPRODUCT_AGENTPRICE) || null,
            unit: product.BPPRODUCT_UNIT,
            assetServiceOwner: product.BPPRODUCT_ASSETSERVICEOWNER,
            variantKey: product.BPPRODUCT_VARIANT_KEY,
            variantName: product.BPPRODUCT_VARIANT_NAME,
            apiStatus: product.BPPRODUCT_APISTATUS || 'draft',
          };
        });

        // Filter by category
        let filteredData = mappedProducts;
        if (selectedCategory !== 'all') {
          filteredData = mappedProducts.filter(item => item.category === selectedCategory);
        }

        // Filter by API publish status
        if (apiStatusFilter !== 'all') {
          filteredData = filteredData.filter(item => item.apiStatus === apiStatusFilter);
        }

        setInventory(filteredData);
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengambil data inventory',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setInventory([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchQuery, toast]);

  useEffect(() => {
    fetchInventory();
    fetchPackages();
  }, [fetchInventory]);

  // Fetch packages from API using batch endpoint (single request)
  const fetchPackages = useCallback(async () => {
    try {
      const response = await packageApi.get('/list-with-details');
      if (response.data.status === 200) {
        const packagesData = response.data.data || [];

        if (packagesData.length === 0) {
          setPackages([]);
          return;
        }

        const transformedPackages = packagesData.map(pkg => ({
          id: pkg.PACKAGE_ID,
          PACKAGE_ID: pkg.PACKAGE_ID,
          PACKAGE_CODE: pkg.PACKAGE_CODE,
          package_name: pkg.PACKAGE_NAME,
          description: pkg.PACKAGE_DESC,
          valid_date: pkg.PACKAGE_VALIDSTARTDATE,
          valid_end_date: pkg.PACKAGE_VALIDENDDATE,
          quota: pkg.PACKAGE_KUOTA,
          items: (pkg.items || []).map(detail => ({
            PACKAGEDETAIL_ID: detail.PACKAGEDETAIL_ID,
            BPPRODUCT_ID: detail.BPPRODUCT_ID,
            BPPRODUCTRETAIL_ID: detail.BPPRODUCTRETAIL_ID,
            quantity: detail.quantity || detail.PACKAGEDETAIL_QUANTITY || 1,
          })),
          total_price: parseFloat(pkg.PACKAGE_BASEPRICE) || 0,
          created_at: pkg.CREATED_TIME,
        }));

        setPackages(transformedPackages);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast({
        title: 'Error',
        description: 'Terjadi kesalahan saat mengambil data packages',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  // Handle Publish Product button click
  const handlePublishProduct = (item) => {
    // Retail publishes straight away: harga dan stok retail tinggal di varian per
    // lokasi, jadi tidak ada yang perlu diisi di modal publish.
    if (item.productType === 'retail') {
      handlePublishConfirm(item, item.apiStatus === 'published');
      return;
    }
    if (item.apiStatus === 'published') {
      // Unpublish directly
      handlePublishConfirm(item, true);
      return;
    }
    // Open publish modal
    setPublishTarget(item);
    setPublishPriceType('publish');
    setPublishPrice(item.price || 0);
    setPublishQuota(item.quantity || 10);
    setPublishValidFrom(item.startDate || new Date().toISOString().split('T')[0]);
    setPublishValidTo(item.endDate || '');
    setPublishLocation(item.location || '');
    setPublishPriceIncludes('');
    setPublishPriceExcludes('');
    onPublishOpen();
  };

  const handlePublishConfirm = async (item, isUnpublish = false) => {
    const isRetail = item.productType === 'retail';
    const api = isRetail ? bpProductRetailApi : bpProductNewApi;
    try {
      if (isUnpublish) {
        const res = await api.post(`/unpublish/${item.id}`);
        if (res.data?.status === 200) {
          toast({ title: 'Unpublished', description: `"${item.name}" ditarik dari API Hub.`, status: 'info', duration: 3000, isClosable: true });
          fetchInventory();
        }
      } else {
        const res = await api.post(`/publish/${item.id}`, isRetail ? undefined : {
          location: publishLocation || null,
          priceIncludes: publishPriceIncludes || null,
          priceExcludes: publishPriceExcludes || null
        });
        if (res.data?.status === 200) {
          toast({ title: 'Published!', description: res.data.message || `"${item.name}" tersedia di API Hub.`, status: 'success', duration: 4000, isClosable: true });
          onPublishClose();
          fetchInventory();
        }
      }
    } catch (err) {
      toast({ title: 'Gagal', description: err.response?.data?.message || err.message, status: 'error', duration: 4000, isClosable: true });
    }
  };

  // Handle Add to Package button click
  const handleAddToPackage = (item) => {
    setSelectedItem(item);
    setItemQuantity(1);
    // For non-retail, use publish price (cannot be changed)
    // For retail, use original price (can be changed)
    const originalPrice = item.price || item.originalPrice || 0;
    setItemPrice(originalPrice);
    setPriceType('bundle');
    setDiscountPercent(0);
    setSelectedPriceType('base'); // Reset to base price
    setSelectedPackageId('');
    fetchPackages();
    onAddToPackageOpen();
  };

  const toggleBulkItemSelection = (itemId) => {
    setSelectedItemIds((prev) => (
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    ));
  };

  const handleOpenBulkAdd = async () => {
    await fetchPackages();
    setBulkPackageId('');
    setBulkQuantity(1);
    onBulkAddOpen();
  };

  // Handle price type selection for non-retail products
  const handleSelectPriceType = (priceType, item) => {
    setSelectedPriceType(priceType);
    let selectedPrice = 0;
    
    if (priceType === 'base') {
      selectedPrice = item.price || item.originalPrice || 0;
    } else if (priceType === 'corporate') {
      selectedPrice = item.corporatePrice || item.price || 0;
    } else if (priceType === 'agent') {
      selectedPrice = item.agentPrice || item.price || 0;
    }
    
    setItemPrice(selectedPrice);
  };

  // Handle Create New Package
  const handleCreateNewPackage = () => {
    setPackageFormData({ package_name: '', description: '', valid_date: '', valid_end_date: '', quota: '', pic_member: '' });
    onAddToPackageClose();
    onNewPackageOpen();
  };

  // Save new package
  const handleSaveNewPackage = async () => {
    if (!packageFormData.package_name.trim()) {
      toast({
        title: 'Validasi gagal',
        description: 'Package name harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await packageApi.post('/', {
        PACKAGE_NAME: packageFormData.package_name,
        PACKAGE_DESC: packageFormData.description || '',
        PACKAGE_VALIDSTARTDATE: packageFormData.valid_date || null,
        PACKAGE_VALIDENDDATE: packageFormData.valid_end_date || null,
        PACKAGE_KUOTA: packageFormData.quota || null,
        PACKAGE_PICMEMBER: packageFormData.pic_member || null,
        PACKAGE_STATUS: '1',
        PACKAGE_BASEPRICE: null,
        PACKAGE_DISCOUNTPRICE: null,
        items: []
      });

      if (response.data.status === 201) {
        setSelectedPackageId(response.data.data.PACKAGE_ID.toString());
        onNewPackageClose();
        onAddToPackageOpen();
        await fetchPackages();
        
        toast({
          title: 'Berhasil',
          description: 'Package baru berhasil dibuat',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error creating package:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat membuat package',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Add item to selected package
  const handleAddItemToPackage = async () => {
    if (!selectedPackageId) {
      toast({
        title: 'Validasi gagal',
        description: 'Pilih package terlebih dahulu',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const packageId = parseInt(selectedPackageId);
    const selectedPkg = packages.find(p => p.id === packageId || p.PACKAGE_ID === packageId);
    
    if (!selectedPkg) {
      toast({
        title: 'Error',
        description: 'Package tidak ditemukan',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Determine which product ID to use based on product type
      const productId = selectedItem.productType === 'retail' 
        ? selectedItem.id 
        : selectedItem.id;
      
      const requestData = {
        PACKAGEDETAIL_STATUS: '1',
        PACKAGEDETAIL_QUANTITY: itemQuantity || 1,
        PACKAGEDETAIL_PRICETYPE: selectedItem.productType === 'retail' ? 'base' : selectedPriceType
      };
      
      if (selectedItem.productType === 'retail') {
        requestData.BPPRODUCTRETAIL_ID = productId;
        requestData.BPPRODUCT_ID = null;
      } else {
        requestData.BPPRODUCT_ID = productId;
        requestData.BPPRODUCTRETAIL_ID = null;
      }

      const response = await packageApi.post(`/${packageId}/add-item`, requestData);

      if (response.data.status === 201) {
        await fetchPackages();
        
        toast({
          title: 'Berhasil',
          description: `Item berhasil ditambahkan ke ${selectedPkg.package_name}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        onAddToPackageClose();
        setSelectedItem(null);
        setSelectedPackageId('');
      }
    } catch (error) {
      console.error('Error adding item to package:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menambahkan item ke package',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleBulkAddToPackage = async () => {
    if (!bulkPackageId) {
      toast({
        title: 'Validasi gagal',
        description: 'Pilih package terlebih dahulu',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const selectedProducts = inventory.filter((item) => selectedItemIds.includes(item.id));
    if (!selectedProducts.length) {
      toast({
        title: 'Tidak ada produk dipilih',
        description: 'Pilih minimal satu produk dari catalog.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      for (const item of selectedProducts) {
        const requestData = {
          PACKAGEDETAIL_STATUS: '1',
          PACKAGEDETAIL_QUANTITY: bulkQuantity || 1,
          PACKAGEDETAIL_PRICETYPE: item.productType === 'retail' ? 'base' : 'base',
          BPPRODUCT_ID: item.productType === 'retail' ? null : item.id,
          BPPRODUCTRETAIL_ID: item.productType === 'retail' ? item.id : null,
        };
        // eslint-disable-next-line no-await-in-loop
        await packageApi.post(`/${parseInt(bulkPackageId, 10)}/add-item`, requestData);
      }
      toast({
        title: 'Bulk add berhasil',
        description: `${selectedProducts.length} produk ditambahkan ke package terpilih.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setSelectedItemIds([]);
      onBulkAddClose();
      await fetchPackages();
    } catch (error) {
      console.error('Error bulk adding items to package:', error);
      toast({
        title: 'Bulk add gagal',
        description: error.response?.data?.message || 'Terjadi kesalahan saat bulk add products.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getCategoryInfo = (categoryId) => {
    return inventoryCategories.find(cat => cat.id === categoryId) || inventoryCategories[0];
  };

  const getCategoryColor = (categoryId) => {
    const colors = {
      'airports': 'blue',
      'aviation-services': 'cyan',
      'hospitality': 'orange',
      'destination-management': 'green',
      'retail': 'purple',
      'tourism-development': 'teal',
    };
    return colors[categoryId] || 'gray';
  };
  const catalogPageSize = 25;
  const catalogPages = Math.max(1, Math.ceil(inventory.length / catalogPageSize));
  const visibleInventory = inventory.slice((catalogPage - 1) * catalogPageSize, catalogPage * catalogPageSize);

  return (
    <Box>
      <VStack align="stretch" spacing={4} mb={6}>
        <HStack justify="space-between" align={{ base: 'start', md: 'end' }} flexDirection={{ base: 'column', md: 'row' }}>
          <Box><Text fontSize="xs" fontWeight="600" color="gray.500">Product Manager / Product Catalog</Text><Heading size="lg" color="gray.900">Product Catalog</Heading><Text mt={1} fontSize="sm" color="gray.500">Jelajahi semua inventory dari berbagai kategori dalam satu tempat.</Text></Box>
        </HStack>
      </VStack>

      {/* Filters */}
      <Card bg="white" borderRadius="16px" boxShadow="sm" mb={6}>
        <CardBody p={{ base: 4, lg: 6 }}>
          <VStack spacing={4} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Cari inventory (nama, deskripsi, lokasi)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size={{ base: 'md', lg: 'lg' }}
                />
              </InputGroup>

              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                size={{ base: 'md', lg: 'lg' }}
              >
                {inventoryCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </Select>

              <Select
                value={apiStatusFilter}
                onChange={(e) => setApiStatusFilter(e.target.value)}
                size={{ base: 'md', lg: 'lg' }}
              >
                <option value="all">Semua Status</option>
                <option value="published">Published to API</option>
                <option value="draft">Draft</option>
              </Select>
            </SimpleGrid>

            <HStack spacing={3} flexWrap="wrap">
              <Button
                leftIcon={<FiRefreshCw />}
                onClick={fetchInventory}
                isLoading={loading}
                variant="outline"
                colorScheme="brand"
                size={{ base: 'sm', lg: 'md' }}
              >
                Refresh
              </Button>
              <Text fontSize="sm" color="gray.500">
                Menampilkan {inventory.length} item
              </Text>
              {selectedItemIds.length > 0 && (
                <>
                  <Button
                    leftIcon={<FiShoppingCart />}
                    size={{ base: 'sm', lg: 'md' }}
                    colorScheme="brand"
                    onClick={handleOpenBulkAdd}
                  >
                    Bulk Add ({selectedItemIds.length})
                  </Button>
                  <Button
                    size={{ base: 'sm', lg: 'md' }}
                    variant="ghost"
                    onClick={() => setSelectedItemIds([])}
                  >
                    Clear Selection
                  </Button>
                </>
              )}
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Inventory Grid */}
      {loading ? (
        <SkeletonBlock lines={8} />
      ) : inventory.length === 0 ? (
        <Card bg="white" borderRadius="16px" boxShadow="sm">
          <CardBody>
            <VStack spacing={4} py={8}>
              <Text color="gray.500" textAlign="center">
                {searchQuery || selectedCategory !== 'all'
                  ? 'Tidak ada inventory yang sesuai dengan filter'
                  : 'Tidak ada data inventory'}
              </Text>
              {(searchQuery || selectedCategory !== 'all') && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('all');
                  }}
                >
                  Reset Filter
                </Button>
              )}
            </VStack>
          </CardBody>
        </Card>
      ) : (
        <>
        <Card bg="white" borderRadius="lg" boxShadow="none" border="1px solid" borderColor="gray.200" overflow="hidden">
          {selectedItemIds.length > 0 && <HStack px={4} py={3} bg="blue.50" borderBottom="1px solid" borderColor="blue.100"><Text fontSize="sm" fontWeight="700" color="blue.800">{selectedItemIds.length} products selected</Text><Button size="xs" colorScheme="blue" leftIcon={<FiShoppingCart />} onClick={handleOpenBulkAdd}>Add to Package</Button><Button size="xs" variant="ghost" onClick={() => setSelectedItemIds([])}>Clear</Button></HStack>}
          <Box overflowX="auto"><Box as="table" w="full" minW="1050px" fontSize="sm"><Box as="thead" bg="gray.50"><Box as="tr"><Box as="th" p={3} textAlign="left"><Checkbox isChecked={visibleInventory.length > 0 && visibleInventory.every((i) => selectedItemIds.includes(i.id))} onChange={(e) => setSelectedItemIds(e.target.checked ? [...new Set([...selectedItemIds, ...visibleInventory.map((i) => i.id)])] : selectedItemIds.filter((id) => !visibleInventory.some((i) => i.id === id)))} /></Box>{['Product','Code','Category','Member','Location','Quantity / Capacity','Price','Status','Action'].map((h) => <Box as="th" key={h} p={3} textAlign="left" color="gray.600" whiteSpace="nowrap">{h}</Box>)}</Box></Box><Box as="tbody">{visibleInventory.map((item) => <Box as="tr" key={item.id} borderTop="1px solid" borderColor="gray.100" _hover={{ bg: 'gray.50' }}><Box as="td" p={3}><Checkbox isChecked={selectedItemIds.includes(item.id)} onChange={() => toggleBulkItemSelection(item.id)} /></Box><Box as="td" p={3} minW="260px"><Button variant="link" colorScheme="blue" fontSize="sm" onClick={() => handleAddToPackage(item)}>{item.name}</Button><Text maxW="300px" noOfLines={1} fontSize="xs" color="gray.500">{item.description}</Text></Box><Box as="td" p={3} fontFamily="mono" fontSize="xs">{item.productCode || '—'}</Box><Box as="td" p={3}>{item.categoryName || getCategoryInfo(item.category).label}</Box><Box as="td" p={3}>{MEMBER_CODE_MAPPING[item.memberCode?.toLowerCase()] || item.memberCode || '—'}</Box><Box as="td" p={3}>{item.location}</Box><Box as="td" p={3}>{item.quantity || 0} {item.unit || ''}</Box><Box as="td" p={3} whiteSpace="nowrap">Rp {(item.price || 0).toLocaleString('id-ID')}</Box><Box as="td" p={3}><Badge colorScheme={item.status === 'active' ? 'green' : item.status === 'pending' ? 'yellow' : 'red'}>{item.status === 'active' ? 'Active' : item.status === 'pending' ? 'Belum Aktif' : 'Inactive'}</Badge></Box><Box as="td" p={3}><Box as="details" position="relative"><Box as="summary" cursor="pointer" listStyleType="none" aria-label={`Actions for ${item.name}`}>•••</Box><VStack position="absolute" right="0" zIndex="20" minW="170px" align="stretch" bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" boxShadow="lg" p={1}><Button size="xs" variant="ghost" justifyContent="start" onClick={() => handleAddToPackage(item)}>Add to Package</Button><Button size="xs" variant="ghost" justifyContent="start" onClick={() => handlePublishProduct(item)}>{item.apiStatus === 'published' ? 'Unpublish' : 'Publish'}</Button></VStack></Box></Box></Box>)}</Box></Box></Box>
          <HStack justify="space-between" p={4} borderTop="1px solid" borderColor="gray.200"><Text fontSize="sm" color="gray.500">Showing {inventory.length ? (catalogPage - 1) * catalogPageSize + 1 : 0}–{Math.min(catalogPage * catalogPageSize, inventory.length)} of {inventory.length} products</Text><HStack><Button size="sm" variant="outline" isDisabled={catalogPage <= 1} onClick={() => setCatalogPage((p) => p - 1)}>Previous</Button><Text fontSize="sm">{catalogPage} / {catalogPages}</Text><Button size="sm" variant="outline" isDisabled={catalogPage >= catalogPages} onClick={() => setCatalogPage((p) => p + 1)}>Next</Button></HStack></HStack>
        </Card>
        <Grid
          display="none"
          templateColumns={{
            base: 'repeat(1, 1fr)',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          }}
          gap={{ base: 4, lg: 6 }}
        >
          {inventory.map((item) => {
            const categoryInfo = getCategoryInfo(item.category);
            const categoryColor = getCategoryColor(item.category);
            
            return (
              <GridItem key={item.id}>
                <Card
                  bg="white"
                  borderRadius="16px"
                  boxShadow="sm"
                  h="100%"
                  _hover={{
                    boxShadow: 'md',
                    transform: 'translateY(-2px)',
                  }}
                  transition="all 0.2s"
                >
                  <CardBody display="flex" flexDirection="column" h="100%">
                    <VStack align="stretch" spacing={4} flex={1}>
                      <Flex justify="space-between" align="start">
                        <HStack spacing={2} flex={1}>
                          <Checkbox
                            isChecked={selectedItemIds.includes(item.id)}
                            onChange={() => toggleBulkItemSelection(item.id)}
                            colorScheme="brand"
                          />
                          {categoryInfo.icon && (
                            <Icon
                              as={categoryInfo.icon}
                              w={5}
                              h={5}
                              color={`${categoryColor}.500`}
                            />
                          )}
                          <VStack align="start" spacing={0} flex={1}>
                            <Text
                              fontSize="xs"
                              fontWeight="600"
                              color={`${categoryColor}.600`}
                              textTransform="uppercase"
                              letterSpacing="wide"
                            >
                              {categoryInfo.label}
                            </Text>
                          </VStack>
                        </HStack>
                        <Badge
                          colorScheme={
                            item.status === 'active' ? 'green' : 
                            item.status === 'pending' ? 'yellow' : 
                            'red'
                          }
                          fontSize={{ base: 'xs', lg: 'sm' }}
                        >
                          {item.status === 'active' ? 'Active' : 
                           item.status === 'pending' ? 'Belum Aktif' : 
                           'Inactive'}
                        </Badge>
                      </Flex>

                      <Heading size="md" color="gray.700" noOfLines={2}>
                        {item.name}
                      </Heading>

                      <Text fontSize="sm" color="gray.600" noOfLines={2}>
                        {item.description}
                      </Text>

                      <VStack align="stretch" spacing={2} pt={2} borderTop="1px solid" borderColor="gray.100" flex={1}>
                        <HStack justify="space-between">
                          <Text fontSize="xs" color="gray.500">
                            Product Code
                          </Text>
                          <Text fontSize="xs" fontWeight="600" color="gray.700">
                            {item.productCode || '-'}
                          </Text>
                        </HStack>
                        {item.categoryName && (
                          <HStack justify="space-between">
                            <Text fontSize="xs" color="gray.500">
                              Category
                            </Text>
                            <Text fontSize="xs" fontWeight="600" color="gray.700">
                              {item.categoryName}
                            </Text>
                          </HStack>
                        )}
                        <HStack justify="space-between">
                          <Text fontSize="xs" color="gray.500">
                            Lokasi
                          </Text>
                          <Text fontSize="xs" fontWeight="600" color="gray.700">
                            {item.location}
                          </Text>
                        </HStack>
                        {item.productType === 'non-retail' && item.quantity && (
                          <HStack justify="space-between">
                            <Text fontSize="xs" color="gray.500">
                              Quantity
                            </Text>
                            <Text fontSize="xs" fontWeight="600" color="gray.700">
                              {item.quantity?.toLocaleString('id-ID') || '0'} {item.unit || ''}
                            </Text>
                          </HStack>
                        )}
                        {item.price > 0 && (
                          <HStack justify="space-between">
                            <Text fontSize="xs" color="gray.500">
                              Price
                            </Text>
                            <Text fontSize="xs" fontWeight="600" color="brand.600">
                              Rp {item.price.toLocaleString('id-ID')}
                            </Text>
                          </HStack>
                        )}
                        {item.startDate && item.endDate && (
                          <>
                            <HStack justify="space-between">
                              <Text fontSize="xs" color="gray.500">
                                Start Date
                              </Text>
                              <Text fontSize="xs" fontWeight="600" color="gray.700">
                                {new Date(item.startDate).toLocaleDateString('id-ID', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </Text>
                            </HStack>
                            <HStack justify="space-between">
                              <Text fontSize="xs" color="gray.500">
                                End Date
                              </Text>
                              <Text fontSize="xs" fontWeight="600" color="gray.700">
                                {new Date(item.endDate).toLocaleDateString('id-ID', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </Text>
                            </HStack>
                          </>
                        )}
                      </VStack>
                    </VStack>
                    
                    <SimpleGrid columns={2} spacing={2} mt={4}>
                      <Button
                        leftIcon={<FiPackage />}
                        colorScheme="brand"
                        size="sm"
                        w="100%"
                        onClick={() => handleAddToPackage(item)}
                      >
                        Add to Package
                      </Button>
                      <Button
                        leftIcon={<FiSend />}
                        colorScheme={item.apiStatus === 'published' ? 'orange' : 'green'}
                        size="sm"
                        w="100%"
                        variant={item.apiStatus === 'published' ? 'outline' : 'solid'}
                        onClick={() => handlePublishProduct(item)}
                      >
                        {item.apiStatus === 'published' ? 'Unpublish' : 'Publish'}
                      </Button>
                    </SimpleGrid>
                  </CardBody>
                </Card>
              </GridItem>
            );
          })}
        </Grid></>
      )}

      {/* Add to Package Modal */}
      <Modal isOpen={isAddToPackageOpen} onClose={onAddToPackageClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add to Package</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedItem && (
              <VStack spacing={4} align="stretch">
                <Card bg="gray.50" p={4}>
                  <VStack align="stretch" spacing={2}>
                    <Text fontWeight="600" fontSize="md">{selectedItem.name}</Text>
                    <Text fontSize="sm" color="gray.600">{selectedItem.description}</Text>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.500">Location:</Text>
                      <Text fontSize="sm" fontWeight="600">{selectedItem.location}</Text>
                    </HStack>
                  </VStack>
                </Card>

                <FormControl>
                  <FormLabel>Select Package</FormLabel>
                  <Select
                    value={selectedPackageId}
                    onChange={(e) => setSelectedPackageId(e.target.value)}
                    placeholder="Pilih package"
                  >
                    {packages.map((pkg) => (
                      <option key={pkg.id || pkg.PACKAGE_ID} value={pkg.id || pkg.PACKAGE_ID}>
                        {pkg.PACKAGE_CODE ? `[${pkg.PACKAGE_CODE}] ` : ''}{pkg.package_name} {pkg.items?.length > 0 && `(${pkg.items.length} items)`}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  leftIcon={<FiPlus />}
                  variant="outline"
                  colorScheme="brand"
                  onClick={handleCreateNewPackage}
                >
                  Create New Package
                </Button>

                {selectedPackageId && (
                  <>
                    <FormControl>
                      <FormLabel>Quantity</FormLabel>
                      <NumberInput
                        value={itemQuantity}
                        onChange={(valueString) => setItemQuantity(parseInt(valueString) || 1)}
                        min={1}
                        max={selectedItem.quantity || 999}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>

                    {/* Price Information - Different for retail vs non-retail */}
                    {selectedItem.productType === 'retail' ? (
                      <>
                        <FormControl>
                          <FormLabel>Base Price (Harga Asli)</FormLabel>
                          <Input
                            value={`Rp ${(selectedItem.price || selectedItem.originalPrice || 0).toLocaleString('id-ID')}`}
                            isReadOnly
                            bg="gray.50"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel>Bundle Price (Rp)</FormLabel>
                          <NumberInput
                            value={itemPrice}
                            onChange={(valueString) => setItemPrice(parseFloat(valueString) || 0)}
                            min={0}
                            precision={0}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Total: Rp {(itemQuantity * itemPrice).toLocaleString('id-ID')}
                          </Text>
                        </FormControl>
                      </>
                    ) : (
                      <>
                        <FormControl>
                          <FormLabel>Publish Price</FormLabel>
                          <Input
                            value={`Rp ${(selectedItem.price || selectedItem.originalPrice || 0).toLocaleString('id-ID')}`}
                            isReadOnly
                            bg="gray.50"
                          />
                        </FormControl>

                        {selectedItem.corporatePrice && (
                          <FormControl>
                            <FormLabel>Corporate Price</FormLabel>
                            <HStack spacing={2}>
                              <Input
                                value={`Rp ${selectedItem.corporatePrice.toLocaleString('id-ID')}`}
                                isReadOnly
                                bg="gray.50"
                                flex={1}
                              />
                              <Button
                                size="sm"
                                colorScheme={selectedPriceType === 'corporate' ? 'blue' : 'gray'}
                                variant={selectedPriceType === 'corporate' ? 'solid' : 'outline'}
                                onClick={() => handleSelectPriceType('corporate', selectedItem)}
                              >
                                Pilih
                              </Button>
                            </HStack>
                          </FormControl>
                        )}

                        {selectedItem.agentPrice && (
                          <FormControl>
                            <FormLabel>Ecosystem Price</FormLabel>
                            <HStack spacing={2}>
                              <Input
                                value={`Rp ${selectedItem.agentPrice.toLocaleString('id-ID')}`}
                                isReadOnly
                                bg="gray.50"
                                flex={1}
                              />
                              <Button
                                size="sm"
                                colorScheme={selectedPriceType === 'agent' ? 'blue' : 'gray'}
                                variant={selectedPriceType === 'agent' ? 'solid' : 'outline'}
                                onClick={() => handleSelectPriceType('agent', selectedItem)}
                              >
                                Pilih
                              </Button>
                            </HStack>
                          </FormControl>
                        )}

                        <FormControl>
                          <FormLabel>Publish Price (Base)</FormLabel>
                          <HStack spacing={2}>
                            <Input
                              value={`Rp ${(selectedItem.price || selectedItem.originalPrice || 0).toLocaleString('id-ID')}`}
                              isReadOnly
                              bg="gray.50"
                              flex={1}
                            />
                            <Button
                              size="sm"
                              colorScheme={selectedPriceType === 'base' ? 'blue' : 'gray'}
                              variant={selectedPriceType === 'base' ? 'solid' : 'outline'}
                              onClick={() => handleSelectPriceType('base', selectedItem)}
                            >
                              Pilih
                            </Button>
                          </HStack>
                        </FormControl>

                        <FormControl>
                          <FormLabel>Selected Price</FormLabel>
                          <Input
                            value={`Rp ${itemPrice.toLocaleString('id-ID')}`}
                            isReadOnly
                            bg="blue.50"
                            borderColor="blue.300"
                          />
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Price Type: {selectedPriceType === 'base' ? 'Publish Price' : selectedPriceType === 'corporate' ? 'Corporate Price' : 'Ecosystem Price'}
                          </Text>
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Total: Rp {(itemQuantity * itemPrice).toLocaleString('id-ID')}
                          </Text>
                        </FormControl>
                      </>
                    )}
                  </>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAddToPackageClose}>
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleAddItemToPackage}
              isDisabled={!selectedPackageId}
            >
              Add to Package
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create New Package Modal */}
      <Modal isOpen={isNewPackageOpen} onClose={onNewPackageClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Package</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Package Name</FormLabel>
                <Input
                  value={packageFormData.package_name}
                  onChange={(e) => setPackageFormData({ ...packageFormData, package_name: e.target.value })}
                  placeholder="Masukkan nama package"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input
                  value={packageFormData.description}
                  onChange={(e) => setPackageFormData({ ...packageFormData, description: e.target.value })}
                  placeholder="Masukkan deskripsi (optional)"
                />
              </FormControl>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl>
                  <FormLabel>Start Date</FormLabel>
                  <Input
                    type="date"
                    value={packageFormData.valid_date}
                    onChange={(e) => setPackageFormData({ ...packageFormData, valid_date: e.target.value })}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>End Date</FormLabel>
                  <Input
                    type="date"
                    value={packageFormData.valid_end_date}
                    onChange={(e) => setPackageFormData({ ...packageFormData, valid_end_date: e.target.value })}
                    min={packageFormData.valid_date || ''}
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel>Kuota</FormLabel>
                  <NumberInput
                    value={packageFormData.quota}
                    onChange={(valueString) => setPackageFormData({ ...packageFormData, quota: valueString })}
                    min={0}
                  >
                    <NumberInputField placeholder="Masukkan kuota" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

              <FormControl>
                <FormLabel>PIC Member</FormLabel>
                <Select
                  value={packageFormData.pic_member}
                  onChange={(e) => setPackageFormData({ ...packageFormData, pic_member: e.target.value })}
                  placeholder="Pilih PIC Member"
                >
                  {Object.entries(MEMBER_CODE_MAPPING).map(([code, name]) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onNewPackageClose}>
              Cancel
            </Button>
            <Button colorScheme="brand" onClick={handleSaveNewPackage}>
              Create Package
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Bulk Add Modal */}
      <Modal isOpen={isBulkAddOpen} onClose={onBulkAddClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Bulk Add to Package</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                {selectedItemIds.length} produk terpilih akan ditambahkan sekaligus.
              </Text>
              <FormControl isRequired>
                <FormLabel>Target Package</FormLabel>
                <Select
                  value={bulkPackageId}
                  onChange={(e) => setBulkPackageId(e.target.value)}
                  placeholder="Pilih package"
                >
                  {packages.map((pkg) => (
                    <option key={pkg.id || pkg.PACKAGE_ID} value={pkg.id || pkg.PACKAGE_ID}>
                      {pkg.PACKAGE_CODE ? `[${pkg.PACKAGE_CODE}] ` : ''}{pkg.package_name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Quantity per Product</FormLabel>
                <NumberInput value={bulkQuantity} min={1} onChange={(valueString) => setBulkQuantity(parseInt(valueString, 10) || 1)}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onBulkAddClose}>
              Cancel
            </Button>
            <Button colorScheme="brand" onClick={handleBulkAddToPackage} isDisabled={!bulkPackageId || selectedItemIds.length === 0}>
              Add All
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Publish Product Modal */}
      <Modal isOpen={isPublishOpen} onClose={onPublishClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Publish Product to API Hub</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {publishTarget && (
                <>
                  <Box bg="gray.50" p={4} borderRadius="md">
                    <Text fontWeight="bold" fontSize="md">{publishTarget.name}</Text>
                    <Text fontSize="sm" color="gray.500">Code: {publishTarget.productCode || '-'}</Text>
                    <Text fontSize="sm" color="gray.500">Member: {publishTarget.memberCode || '-'}</Text>
                  </Box>
                  <FormControl>
                    <FormLabel>Pricing Tier</FormLabel>
                    <Select value={publishPriceType} onChange={(e) => {
                      setPublishPriceType(e.target.value);
                      const tier = e.target.value;
                      const price = tier === 'corporate' ? (publishTarget.corporatePrice || 0) :
                                    tier === 'agent' ? (publishTarget.agentPrice || 0) :
                                    (publishTarget.price || 0);
                      setPublishPrice(price);
                    }}>
                      <option value="publish">Publish Price {publishTarget.price > 0 ? `(Rp ${publishTarget.price.toLocaleString('id-ID')})` : ''}</option>
                      <option value="corporate">Corporate Price {publishTarget.corporatePrice > 0 ? `(Rp ${publishTarget.corporatePrice.toLocaleString('id-ID')})` : '(not set)'}</option>
                      <option value="agent">Agent Price {publishTarget.agentPrice > 0 ? `(Rp ${publishTarget.agentPrice.toLocaleString('id-ID')})` : '(not set)'}</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Price (Rp)</FormLabel>
                    <NumberInput value={publishPrice} onChange={(v) => setPublishPrice(Number(v))} min={0}>
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Quota</FormLabel>
                    <NumberInput value={publishQuota} onChange={(v) => setPublishQuota(Number(v))} min={0}>
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Lokasi</FormLabel>
                    <Input placeholder="Contoh: Jakarta, CGK, Magelang" value={publishLocation} onChange={(e) => setPublishLocation(e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Price Includes</FormLabel>
                    <Textarea placeholder="Contoh: Tiket masuk, welcome drink" value={publishPriceIncludes} onChange={(e) => setPublishPriceIncludes(e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Price Excludes</FormLabel>
                    <Textarea placeholder="Contoh: Transportasi, oleh-oleh" value={publishPriceExcludes} onChange={(e) => setPublishPriceExcludes(e.target.value)} />
                  </FormControl>
                  <SimpleGrid columns={2} spacing={4}>
                    <FormControl>
                      <FormLabel>Valid From</FormLabel>
                      <Input type="date" value={publishValidFrom} onChange={(e) => setPublishValidFrom(e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Valid To</FormLabel>
                      <Input type="date" value={publishValidTo} onChange={(e) => setPublishValidTo(e.target.value)} />
                    </FormControl>
                  </SimpleGrid>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPublishClose}>Cancel</Button>
            <Button colorScheme="green" leftIcon={<FiSend />} onClick={() => publishTarget && handlePublishConfirm(publishTarget)}>
              Publish to API
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default BrowseInventory;

